import React from 'react';
import {
    Flex,
    ReadMore,
    useStyles,
    pxToRem,
    pxArrayToRem,
    Grid,
} from '@archipro-design/aria';
import { Link } from '@remix-run/react';
import * as S from './Content.style';
import { useIsSafari } from '@modules/root/hook/use-is-safari';
import { ArrowRightLine } from '@archipro-design/icons';
import Stats from './Stats';
import CompanyInfoStats from './CompanyInfoStats';
import { useWebsiteTrackingProxy } from '~/modules/tracking/hook/useWebsiteTrackingProxy';

import { useLoaderData } from '@remix-run/react';
import type { loader } from '~/routes/_app.product.$urlSegment';
import type { ProductProfessional } from '../../type/product-professional';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '~/modules/professional/util/constants';

const MAX_LINES = 9;

export type ContentData = Pick<
    ProductProfessional,
    | 'ID'
    | 'Title'
    | 'BlockSummary'
    | 'URLSegment'
    | 'LibraryLocations'
    | 'ProductCount'
    | 'EnquiryResponseRate'
    | 'EnquiryAvgResponseHours'
    | 'HasArticles'
    | 'HasPartners'
    | 'HasProjects'
    | 'HasVideos'
>;

interface ContentProps {
    data: ContentData;
}

interface ListProps {
    label: string;
    link: string;
}

const Content = ({ data }: ContentProps) => {
    const [visible, setVisible] = React.useState(false);
    const isSafari = useIsSafari();
    const { css } = useStyles({ isSafari, visible });
    const links = generateLinks(data);
    const { product } = useLoaderData<typeof loader>();
    const clickProxy = useWebsiteTrackingProxy({
        professionalID: product.Professional.ID,
        professionalTitle: product.Professional.Title,
        itemType: 'Product',
    });

    return (
        <Flex column={false} vAlign="center">
            <Grid
                columns={pxArrayToRem([880, 896])}
                className={css({
                    columnGap: pxToRem(44),
                })}
            >
                <div>
                    <div
                        onMouseEnter={() => setVisible(true)}
                        onMouseLeave={() => setVisible(false)}
                        className="block w-[27.125rem] mb-[8rem]"
                    >
                        {links.map((link) => (
                            <Link
                                to={link.link}
                                key={link.label}
                                prefetch="render"
                                className={`
                    flex items-center mb-0 h-[3.375rem] w-full
                    border-b transition-colors duration-300 group
                    ${
                        visible
                            ? 'border-black/50 hover:border-black'
                            : 'border-black hover:border-black/50'
                    }
                `}
                            >
                                <span
                                    className={`
                        flex-1 text-[1.25rem] text-charcoal-000 tracking-[-0.01em]
                        transition-opacity duration-300
                        ${visible ? 'opacity-50 group-hover:opacity-100' : 'opacity-100 group-hover:opacity-50'}
                    `}
                                >
                                    {link.label}
                                </span>
                                <ArrowRightLine
                                    className={`
                        relative -bottom-[0.0625rem] w-[1.875rem] h-[1.875rem]
                        opacity-0 mr-[0.5rem]
                        group-hover:opacity-100 group-hover:mr-0
                        transition-[opacity,margin] duration-300 ease-in-out
                    `}
                                />
                            </Link>
                        ))}
                    </div>

                    <Stats data={data} />
                </div>

                <div className={css(S.AboutTextContainer)}>
                    <ReadMore
                        content={{
                            children: (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: data.BlockSummary ?? '',
                                    }}
                                    onClick={clickProxy}
                                    onContextMenu={clickProxy}
                                />
                            ),
                        }}
                        maxLines={MAX_LINES}
                        className={css(S.AboutTextContent)}
                        variables={{
                            toggleFontSize: pxToRem(16),
                        }}
                    />
                    <CompanyInfoStats data={data} />
                </div>
            </Grid>
        </Flex>
    );
};

const generateLinks = (data: ContentData): ListProps[] => {
    const BASE_COMPANY_URL = `/professional/${data.URLSegment}`;
    return [
        {
            label: 'Overview',
            link: BASE_COMPANY_URL,
        },
        {
            label: 'About',
            link: `${BASE_COMPANY_URL}/info#${PROFESSIONAL_SECTIONS_ANCHOR}`,
        },
        {
            label: 'Products',
            link: `${BASE_COMPANY_URL}/our-products#${PROFESSIONAL_SECTIONS_ANCHOR}`,
        },
        ...(data.HasProjects
            ? [
                  {
                      label: 'Projects',
                      link: `${BASE_COMPANY_URL}/case-studies#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        ...(data.HasArticles
            ? [
                  {
                      label: 'Articles',
                      link: `${BASE_COMPANY_URL}/journal#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        ...(data.HasVideos
            ? [
                  {
                      label: 'Videos',
                      link: `${BASE_COMPANY_URL}/videos#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        ...(data.HasPartners
            ? [
                  {
                      label: 'Network',
                      link: `${BASE_COMPANY_URL}/partners#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        {
            label: 'Contact',
            link: `${BASE_COMPANY_URL}/contact#${PROFESSIONAL_SECTIONS_ANCHOR}`,
        },
    ];
};

export default Content;
